import React, { useMemo } from "react";
import axios from "../../api/axiosInstance";
import AddItemPopup from "../../purchasing/components/popups/AddItemPopup";

const ITEM_FIELDS = ["supplier_code", "description", "category_id", "subcategory_id",
  "unit_id", "subunit_id", "pack_size", "subunit2_id", "subunit2_size", "minimum_purchase_quantity", "purchase_multiple"];

// Reuse purchasing details while keeping inventory decisions in each business unit.
export function createCorporateItemAdapter(record, catalog, measurementUnits = []) {
  const classificationRecords = [...catalog];
  let savedRecordId = record?.id;
  const byId = new Map(classificationRecords.map((entry) => [Number(entry.id), entry]));
  const withHierarchy = (entry) => {
    if (!entry) return null;
    const value = { ...entry, ...entry.data };
    const parent = byId.get(Number(entry.parent_id));
    if (entry.kind === "subfamily") { value.family_id = entry.parent_id; value.family = withHierarchy(parent); }
    if (entry.kind === "category") { value.subfamily_id = entry.parent_id; value.subfamily = withHierarchy(parent); }
    if (entry.kind === "subcategory") {
      value.category_id = entry.parent_id;
      value.category = withHierarchy(parent);
    }
    return value;
  };
  const options = (kind) => classificationRecords.filter((entry) => entry.kind === kind && entry.is_active !== false &&
    (kind !== "supplier" || entry.data?.is_stock_supplier !== false)).map(withHierarchy);
  return {
    languageId: "en",
    title: record?.id ? "Edit Item" : "New Catalog Item",
    hint: "Set the item details, purchasing units and suppliers.",
    requireCategory: true,
    inventoryPolicyEnabled: false,
    assetContext: { scope: "corporate", ...(record?.id ? { catalog_record_id: record.id } : {}) },
    initialItem: {
      code: record?.code || "", name: record?.name || "",
      ...Object.fromEntries(ITEM_FIELDS.map((key) => [key, record?.data?.[key] ?? ""])),
      suppliers: record?.data?.supplier_ids || [],
      unit_justifications: record?.data?.unit_justifications || {},
      product_details: record?.data?.product_details ?? null,
      category: withHierarchy(byId.get(Number(record?.data?.category_id))),
      subcategory: withHierarchy(byId.get(Number(record?.data?.subcategory_id))),
    },
    loadOptions: async () => {
      const commonUnits = measurementUnits.filter((unit) => unit.source === "purchasing" && unit.business_unit_id == null && unit.is_active !== false && !unit.is_hidden);
      const units = options("unit").filter((unit) => !unit.data?.purchasing_unit_id && !unit.is_hidden);
      for (const unit of commonUnits) {
        const reference = catalog.find((entry) => entry.kind === "unit" && Number(entry.data?.purchasing_unit_id) === Number(unit.id));
        units.push({ ...unit, id: reference?.id || `common-${unit.id}` });
      }
      return [options("category"), units, options("supplier")];
    },
    loadClassification: async () => ["family", "subfamily", "category", "subcategory"].map(options),
    createClassification: async (kind, payload) => {
      const { code, name, description } = payload;
      const parentField = { subfamily: "family_id", category: "subfamily_id", subcategory: "category_id" }[kind];
      const response = await axios.post("/api/admin/purchasing/catalog", {
        kind, code, name, parent_id: parentField ? Number(payload[parentField]) : null,
        data: { description },
      });
      classificationRecords.push(response.data);
      byId.set(Number(response.data.id), response.data);
      return withHierarchy(response.data);
    },
    loadSubcategories: async (categoryId) => options("subcategory").filter((entry) => Number(entry.category_id) === Number(categoryId)),
    saveItem: async (payload, supplierIds) => {
      const { code, name, ...itemFields } = payload;
      const data = { ...record?.data, ...itemFields, supplier_ids: supplierIds };
      // Old corporate records and callers may still contain local inventory fields.
      for (const field of ["inventory_policy", "consumption_frequencies", "par_stock"]) delete data[field];
      const resolved = new Map();
      for (const field of ["unit_id", "subunit_id", "subunit2_id"]) {
        const selection = String(data[field] || "");
        if (!selection.startsWith("common-")) continue;
        const id = Number(selection.slice(7));
        if (!resolved.has(id)) {
          const response = await axios.post(`/api/admin/purchasing/units/${id}/catalog-reference`);
          resolved.set(id, response.data.id);
        }
        data[field] = resolved.get(id);
      }
      if (record?.data?.supplier_details) {
        data.supplier_details = Object.fromEntries(supplierIds.filter((id) => record.data.supplier_details[id])
          .map((id) => [id, record.data.supplier_details[id]]));
      }
      const body = { kind: "item", code, name, parent_id: null, data };
      const response = savedRecordId
        ? await axios.put(`/api/admin/purchasing/catalog/${savedRecordId}`, body)
        : await axios.post("/api/admin/purchasing/catalog", body);
      savedRecordId = response.data.id;
      return response.data;
    },
  };
}

export default function CorporateItemEditor({ record, catalog, measurementUnits, onClose, onSaved }) {
  const adapter = useMemo(() => createCorporateItemAdapter(record, catalog, measurementUnits), [record, catalog, measurementUnits]);
  return <AddItemPopup isOpen onClose={onClose} onAdded={onSaved} editorAdapter={adapter} />;
}
